// SCR-STAFF-995 — گزارش کانال‌ها (US-CHN-050؛ AC-CHN-050.1؛ AP-CHN-02): پرتال، ویجت، مرکز تماس و تیکتینگ در بازه تاریخ شمسی و شعبه فعال (`chn.report.view`). شاخصی
// که داده‌اش هنوز در هیچ Contract نیست «ناموجود» نمایش داده می‌شود (C-AGENT-1512)؛ کیوسک فاز 4 است.
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type ChnKeyCount } from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation, type TFunction } from '@phoenix/i18n';
import { ErrorState, faDigits, ListPage, LoadingState, toBusinessDate } from '@phoenix/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { formatJalaliInput, parseJalaliInput } from '../patients/common';
import { addDays, percent, useActiveBranch } from '../scheduling/common';
import { categoryLabel, SCR_STAFF_995 } from './ticketing';

type Value = number | string | null | undefined;

function count(value: Value): string {
  return value === null || value === undefined ? '' : faDigits(String(Number(value)));
}

function decimal(value: Value): string | null {
  return value === null || value === undefined ? null : faDigits(String(Math.round(Number(value) * 10) / 10));
}

function share(value: Value): string | null {
  return value === null || value === undefined ? null : percent(value);
}

function Metric({ labelKey, value, testId }: { labelKey: string; value: string | null; testId: string }) {
  const { t } = useTranslation('chn');
  return (
    <Box sx={{ minWidth: 150, flex: 1 }}>
      <Typography variant="caption" color="text.secondary" component="p">
        {t(labelKey)}
      </Typography>
      <Typography variant="h6" component="p" data-testid={testId} color={value === null ? 'text.secondary' : undefined}>
        {value ?? t('chn:report.unavailable')}
      </Typography>
    </Box>
  );
}

function Section({ titleKey, id, children }: { titleKey: string; id: string; children: ReactNode }) {
  const { t } = useTranslation('chn');
  return (
    <Card variant="outlined" component="section" aria-labelledby={`chn-report-${id}`}>
      <CardContent>
        <Typography id={`chn-report-${id}`} variant="h6" component="h2" gutterBottom>
          {t(titleKey)}
        </Typography>
        {children}
      </CardContent>
    </Card>
  );
}

function Breakdown({ titleKey, items, label }: { titleKey: string; items: readonly ChnKeyCount[]; label: (key: string) => string }) {
  const { t } = useTranslation('chn');
  return (
    <Box sx={{ minWidth: 200, flex: 1 }}>
      <Typography variant="subtitle2" component="h3">
        {t(titleKey)}
      </Typography>
      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('chn:report.no_data')}
        </Typography>
      ) : (
        <Box component="ul" sx={{ m: 0, pl: 2 }}>
          {items.map((item) => (
            <li key={item.key}>
              <Typography variant="body2">
                {label(item.key)}: {count(item.count)}
              </Typography>
            </li>
          ))}
        </Box>
      )}
    </Box>
  );
}

function sourceLabel(t: TFunction, key: string): string {
  return key === 'direct' ? t('chn:report.widget.direct') : key;
}

export function ChannelReportPage() {
  const { t } = useTranslation('chn');
  const { tenantApi, session } = useTenantSession();
  const { branchId } = useActiveBranch();
  const today = toBusinessDate(new Date());
  const [from, setFrom] = useState(() => formatJalaliInput(addDays(today, -30)));
  const [to, setTo] = useState(() => formatJalaliInput(today));
  const fromDate = parseJalaliInput(from);
  const toDate = parseJalaliInput(to);
  const report = useQuery({
    queryKey: ['chn', session?.tenantId, 'report', fromDate, toDate, branchId],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/channels/reports/channels', { params: { query: { from: fromDate ?? '', to: toDate ?? '', branchId: branchId ?? undefined } } })),
    enabled: fromDate !== undefined && toDate !== undefined,
    placeholderData: keepPreviousData,
  });
  const data = report.data;

  return (
    <ListPage
      screenId={SCR_STAFF_995}
      titleKey="chn:report.title"
      subtitleKey="chn:report.subtitle"
      filters={
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField size="small" label={t('chn:report.from')} value={from} onChange={(event) => setFrom(event.target.value)} error={fromDate === undefined} slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'chn-report-from' } }} />
          <TextField size="small" label={t('chn:report.to')} value={to} onChange={(event) => setTo(event.target.value)} error={toDate === undefined} slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'chn-report-to' } }} />
        </Stack>
      }
    >
      {report.isPending ? <LoadingState rows={4} /> : null}
      {report.error ? <ErrorState error={report.error} onRetry={() => void report.refetch()} /> : null}
      {data ? (
        <Stack spacing={2}>
          <Section titleKey="chn:report.tickets.title" id="tickets">
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={2}>
              <Metric labelKey="chn:report.tickets.created" value={count(data.tickets.created)} testId="chn-report-tickets-created" />
              <Metric labelKey="chn:report.tickets.resolved" value={count(data.tickets.resolved)} testId="chn-report-tickets-resolved" />
              <Metric labelKey="chn:report.tickets.sla_compliance" value={share(data.tickets.slaCompliance)} testId="chn-report-tickets-sla" />
              <Metric labelKey="chn:report.tickets.first_response_compliance" value={share(data.tickets.firstResponseCompliance)} testId="chn-report-tickets-first" />
              <Metric labelKey="chn:report.tickets.average_resolution" value={decimal(data.tickets.averageResolutionHours)} testId="chn-report-tickets-resolution" />
              <Metric labelKey="chn:report.tickets.average_satisfaction" value={decimal(data.tickets.averageSatisfaction)} testId="chn-report-tickets-satisfaction" />
              <Metric labelKey="chn:report.tickets.open_breached" value={count(data.tickets.openBreached)} testId="chn-report-tickets-breached" />
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 2 }}>
              <Breakdown titleKey="chn:report.tickets.by_type" items={data.tickets.byType} label={(key) => t(`chn:tickets.type.${key}`, { defaultValue: key })} />
              <Breakdown titleKey="chn:report.tickets.by_category" items={data.tickets.byCategory} label={(key) => categoryLabel(t, key, null)} />
              <Breakdown titleKey="chn:report.tickets.by_source" items={data.tickets.bySource} label={(key) => t(`chn:tickets.source.${key}`, { defaultValue: key })} />
            </Stack>
          </Section>
          <Section titleKey="chn:report.calls.title" id="calls">
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={2}>
              <Metric labelKey="chn:report.calls.inbound" value={count(data.calls.inbound)} testId="chn-report-calls-inbound" />
              <Metric labelKey="chn:report.calls.outbound" value={count(data.calls.outbound)} testId="chn-report-calls-outbound" />
              <Metric labelKey="chn:report.calls.answered" value={count(data.calls.answered)} testId="chn-report-calls-answered" />
              <Metric labelKey="chn:report.calls.missed" value={count(data.calls.missed)} testId="chn-report-calls-missed" />
              <Metric labelKey="chn:report.calls.average_duration" value={decimal(data.calls.averageDurationSeconds)} testId="chn-report-calls-duration" />
              <Metric labelKey="chn:report.calls.callbacks" value={count(data.calls.callbacksNeeded)} testId="chn-report-calls-callbacks" />
            </Stack>
            <Box sx={{ mt: 2 }}>
              <Breakdown titleKey="chn:report.calls.reasons" items={data.calls.reasons} label={(key) => t(`chn:calls.reason.${key}`, { defaultValue: key })} />
            </Box>
          </Section>
          <Section titleKey="chn:report.portal.title" id="portal">
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={2}>
              <Metric labelKey="chn:report.portal.active_patients" value={data.portal.activePatients === null ? null : count(data.portal.activePatients)} testId="chn-report-portal-active" />
              <Metric labelKey="chn:report.portal.online_booking_share" value={share(data.portal.onlineBookingShare)} testId="chn-report-portal-booking" />
              <Metric labelKey="chn:report.portal.online_payment_share" value={share(data.portal.onlinePaymentShare)} testId="chn-report-portal-payment" />
              <Metric labelKey="chn:report.portal.pre_visit_form_share" value={share(data.portal.preVisitFormShare)} testId="chn-report-portal-forms" />
            </Stack>
          </Section>
          <Section titleKey="chn:report.widget.title" id="widget">
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={2}>
              <Metric labelKey="chn:report.widget.visits" value={data.widget.visits === null ? null : count(data.widget.visits)} testId="chn-report-widget-visits" />
              <Metric labelKey="chn:report.widget.bookings" value={count(data.widget.bookings)} testId="chn-report-widget-bookings" />
              <Metric labelKey="chn:report.widget.conversion" value={share(data.widget.conversionRate)} testId="chn-report-widget-conversion" />
              <Metric labelKey="chn:report.widget.web_forms" value={count(data.widget.webForms)} testId="chn-report-widget-forms" />
            </Stack>
            <Box sx={{ mt: 2 }}>
              <Breakdown titleKey="chn:report.widget.sources" items={data.widget.sources} label={(key) => sourceLabel(t, key)} />
            </Box>
          </Section>
          <Alert severity="info" variant="outlined">
            {t('chn:report.kiosk_phase4')}
          </Alert>
        </Stack>
      ) : null}
    </ListPage>
  );
}
